import React, { useEffect, useState } from 'react';
import { getCurrentUser, getSnippets } from '../utils/api';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { motion } from 'framer-motion';

export default function Dashboard() {
  const [credits, setCredits] = useState(null);
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [username, setUsername] = useState('');
  const [accessToken, setAccessToken] = useState('');

useEffect(() => {
  async function fetchData() {
    const user = await getCurrentUser();
    const list = await getSnippets();
    setCredits(user.credits);
    setSnippets(list);
    setUsername(user.email.split('@')[0]);
    setAccessToken(user.access_token);

    // 🔒 Automatically copy access token to clipboard
    if (user.access_token) {
      navigator.clipboard.writeText(user.access_token).catch((err) => {
        console.warn('Failed to copy token:', err);
      });
    }

    setLoading(false);
  }
  fetchData();
}, []);


  const handleSelectSnippet = (snippet) => {
    setSelectedSnippet(snippet);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        maxWidth: '1150px',
        margin: '32px auto',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24,
      }}
    >
      {/* LEFT PANEL: User Info & Snippet List */}
      <Paper
        elevation={6}
        sx={{ p: 4, borderRadius: 5, flex: '1 1 250px', maxHeight: '600px', overflowY: 'auto' }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Welcome, <span style={{ color: '#16f288' }}>{username}!</span>
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <b>Credits:</b>{' '}
          <span style={{ color: '#16f288', fontWeight: 'bold' }}>{credits}</span>
        </Typography>
        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          Snippets
        </Typography>
        <List>
          {snippets.map((s) => (
            <ListItem key={s.keyword} disablePadding>
              <ListItemButton onClick={() => handleSelectSnippet(s)}>
                <ListItemText primary={s.keyword} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* RIGHT PANEL: Expanded Snippet Content, Token, Buy Credits */}
      <Box sx={{ flex: '2 1 600px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Expanded Snippet Viewer */}
        {selectedSnippet && (
          <Paper elevation={6} sx={{ p: 4, borderRadius: 5 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Snippet: <span style={{ color: '#16f288' }}>{selectedSnippet.keyword}</span>
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: '#2b2f45',
                borderRadius: 2,
                fontFamily: 'monospace',
                fontSize: 15,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '300px',
                overflowY: 'auto',
              }}
            >
              {selectedSnippet.content}
            </Box>
          </Paper>
        )}

        {/* Copy API Token Box */}
        {accessToken && (
          <Paper elevation={6} sx={{ p: 4, borderRadius: 5 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              API Token
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                onClick={() => navigator.clipboard.writeText(accessToken)}
                variant="contained"
                color="success"
                sx={{ width: 'fit-content' }}
              >
                Copy API Token for Python App
              </Button>
              <Typography variant="caption">
                Paste this token into your Neo Typer desktop app to log in instantly!
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Buy Credits Box */}
        <Paper elevation={6} sx={{ p: 4, borderRadius: 5 }}>
          <Typography variant="h5">Buy Credits</Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            To buy more credits, DM us on{' '}
            <a
              href="https://www.instagram.com/j.heibs/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#16f288', fontWeight: 600, textDecoration: 'underline' }}
            >
              @neotyper_official
              <span style={{ verticalAlign: 'middle', marginLeft: 3 }}>
                <img
                  src="https://imagepng.org/wp-content/uploads/2017/08/instagram-icone-icon.png"
                  alt="Instagram"
                  width={18}
                />
              </span>
            </a>
            <br />
            <b>Get Started:</b> 1 credit = 1 solution — simple & transparent.  
            <br />
            🎁 <b>Limited Offer:</b> Get <span style={{ color: '#16f288' }}>5 bonus credits</span> on your each purchase!  
            <br />
            DM us now for instant top-up — fast, secure & trusted by 12+ users.
          </Typography>
        </Paper>
                            {/* Download App Box */}
        <Paper elevation={6} sx={{ p: 4, borderRadius: 5 }}>
         <Typography variant="h5">Download Neo Typer App</Typography>

          <Typography variant="body1" sx={{ mt: 2 }}>
            Click the button below to download the latest version of the Neo Typer desktop application.
          </Typography>
          
          <Typography variant="body1" sx={{ mt: 2 }}>
            🎉 Congrats! The Pro Version has been released — and it's totally free for now. Give it a try!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            href="https://www.mediafire.com/file/halzm8wxsqiyagp/Neo_Typer_Pro.exe/file"
            target="_blank"
            rel="noopener noreferrer"
          >
            ⬇ Download for Windows
          </Button>
        </Paper>

      </Box>
    </motion.div>
  );
}
